import Button from "common/Buttons/Button";
import {
  BotType,
  FiltersSlice,
  LanguageNameSpaces,
  SubscriptionPlanType,
} from "@shared/types";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import FilterOutlined from "@ant-design/icons/FilterOutlined";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import TableOutlined from "@ant-design/icons/TableOutlined";
import UserAddOutlined from "@ant-design/icons/UserAddOutlined";
import UsergroupAddOutlined from "@ant-design/icons/UsergroupAddOutlined";
import React, {
  CSSProperties,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";
import { Copy, Plus } from "lucide-react";
import PageIntroActions from "features/bots/NewLook/PageIntroActions";
import { getNewLookTokens, NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { NewLookBotsToolbarContext } from "features/bots/NewLook/NewLookBotsToolbar.context";
import NewLookFiltersButton from "features/bots/NewLook/NewLookFiltersButton";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_PAGE_GUTTER,
  NEW_LOOK_SECTION_GAP,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout";
import {
  BULK_CREATE_BOTS_FEATURE_FLAG,
  DEFAULT_PADDING,
  INTERCOM_BOTTOM,
  IS_WHOP,
  MOBILE_SEARCH,
  SMALL_MOBILE,
  SMALL_MOBILE_PADDING,
} from "common/constants";
import { useHistory } from "react-router-dom";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Text from "common/Typography/Text";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import useMedia from "utils/hooks/useMedia";
import Flex from "common/Boxes/Flex";
import useSearchInput from "utils/hooks/useSearchInput";
import { CopyToClipboardButton } from "common/CopyToClipboard/CopyToClipboard";
import SortSelect from "common/Select/SortSelect";
import StickyHeader from "common/Headers/StickyHeader";
import { SINGLE_FILTERS_ROW_BREAKPOINT } from "@shared/features/filters/constants";
import FiltersColumn from "common/Filters/FiltersColumn/FiltersColumn";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import {
  groupConfigurationsFilters,
  publicBotsFilters,
} from "@shared/slices/filters.slice";
import {
  BOT_TYPE_TO_FILTERS_SLICE,
  botsFilterTypeToTitle,
} from "@shared/features/bots/constants";
import useQuery from "utils/hooks/useQuery";
import BulkActions from "common/Table/BulkActions";
import { BotsBulkActionsContext } from "@shared/features/bots/BotsBulkActionsProvider";
import {
  getCreateBotRoute,
  getCreateChannelConfigurationsPath,
} from "utils/routes";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import { useDispatch, useSelector } from "react-redux";
import FiltersCounter from "common/Filters/common/FiltersCounter";
import LayoutSwitch from "common/Switch/LayoutSwitch";
import Dropdown from "common/Dropdown/Dropdown";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { sharedLib } from "@shared/services/shared.service";
import { HEADER_HEIGHT } from "common/Home/General/constants.tsx";
import { getTvWebhookBaseUrl } from "app/shared/utils";
import { useBottomNavigator } from "utils/hooks/misc";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useCanManageBotType } from "@shared/features/bots/hooks";
import { useSubUserPermissions } from "@shared/utils/hooks/misc";
import {
  MY_BOTS_BULK_CREATE_BUTTON_ID,
  MY_BOTS_CREATE_BOT_BUTTON_ID,
  MY_BOTS_CREATE_SINGLE_BUTTON_ID,
} from "testIds.ts";

interface BotsPageWrapperProps {
  isCardsView?: boolean;
  setIsCardsView?: (isCardsView: boolean) => void;
  setSearchValue?: (searchValue: string) => void;
  loading: boolean;
  showFilters?: boolean;
  setShowFilters?: (showFilters: boolean) => void;
  useFiltersChips: boolean;
  padding?: number;
  isSummaryTabs?: boolean;
  style?: CSSProperties;
}

const BotsPageWrapper: FC<PropsWithChildren<BotsPageWrapperProps>> = ({
  isCardsView,
  setIsCardsView,
  setSearchValue,
  children,
  loading,
  showFilters,
  setShowFilters,
  useFiltersChips,
  padding: propsPadding,
  isSummaryTabs,
  style,
}) => {
  const {
    botType,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    sortOptions,
    isChannelConfiguration,
    isPublicBots,
    getBotsDataStatus,
  } = useContext(MyBotsPageContext)!;
  const {
    isBulkMode,
    selectedBotsIDs,
    setSelectedBotsIDs,
    onSelectUnselectAllPress,
    exitBulkUpdate,
    actionButtonsData,
    isAllSelected,
  } = useContext(BotsBulkActionsContext)!;
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t: tBots, i18n } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { historyPush } = useHistoryWithQuery();
  const query = useQuery();
  const queryOpenFilters = query.get("open-filters");
  const { distanceTop } = useContext(PageWrapperContext);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isSingleFiltersChipsRow =
    useMedia(
      [`(max-width: ${SINGLE_FILTERS_ROW_BREAKPOINT})`],
      [true],
      false
    ) ||
    isBottomNavigator ||
    isSummaryTabs;
  const mobileSearch = useMedia(
    [`(max-width: ${MOBILE_SEARCH})`],
    [true],
    false
  );
  const showSortInActionsRow =
    useMedia([`(max-width: 1060px)`], [false], true) && !isPublicBots;
  const paddingByScreenSize = useMedia(
    [`(max-width: ${SMALL_MOBILE})`],
    [SMALL_MOBILE_PADDING],
    DEFAULT_PADDING
  );
  const padding = propsPadding ?? paddingByScreenSize;
  const [SearchInput, searchValue] = useSearchInput({
    placeholder: t("labels.search_placeholder"),
    style: { width: "100%", borderRadius: 8 },
  });
  const [noExchangeModalVisible, setNoExchangeModalVisible] = useState(false);
  const canManageBotType = useCanManageBotType(botType);
  const { isReadOnly } = useSubUserPermissions();
  const { isNewLook } = useTheme();
  // New look (desktop): page-level actions move to the page intro and the
  // content gets the canvas gutter. The form's public bots tab keeps the
  // classic layout.
  const isNewLookPage = isNewLook && !isBottomNavigator && !isSummaryTabs;
  const filtersReady =
    !isNewLook ||
    isPublicBots ||
    isChannelConfiguration ||
    !!getBotsDataStatus.currentData ||
    getBotsDataStatus.isError;

  useEffect(() => {
    if (queryOpenFilters === "true" && !showFilters) setShowFilters?.(true);
  }, [queryOpenFilters]);

  useEffect(() => {
    setSearchValue?.(searchValue || "");
  }, [searchValue]);

  useEffect(() => {
    if (!useFiltersChips) {
      setShowFilters?.(true);
    }
  }, [useFiltersChips]);

  const onClick = (isBulk?: boolean) => {
    if (isChannelConfiguration) {
      historyPush(getCreateChannelConfigurationsPath());
    } else if (user?.has_valid_clients_connected || IS_WHOP) {
      history.push(getCreateBotRoute(botType, isBulk));
    } else {
      setNoExchangeModalVisible(true);
    }
  };

  const getCreateBotButton = (inline?: boolean, quiet?: boolean) => {
    if (!canManageBotType || (isBottomNavigator && isBulkMode)) return;
    const isFloating = isSingleFiltersChipsRow && !inline;
    // New look, phone: no floating button over the cards; the header's "+"
    // creates bots.
    if (isFloating && isNewLook && isBottomNavigator) return;
    const isDropdown =
      botType !== BotType.SIGNAL &&
      botType !== BotType.TRADING_VIEW_SIGNALS &&
      (user?.hasFeatureFlag(BULK_CREATE_BOTS_FEATURE_FLAG) ||
        user?.subscription_plan_type === SubscriptionPlanType.ASSET_MANAGER);
    const buttonBottom =
      isBottomNavigator || mobileSearch ? 16 : INTERCOM_BOTTOM;
    const button = (
      <Button
        type={quiet ? "default" : "primary"}
        id="create-bot"
        data-testid={MY_BOTS_CREATE_BOT_BUTTON_ID}
        onClick={isDropdown ? undefined : () => onClick()}
        shape={isFloating ? "circle" : "default"}
        style={
          isFloating
            ? {
                position: isBottomNavigator ? "absolute" : "fixed",
                right: mobileSearch ? 16 : 90,
                bottom: buttonBottom,
                zIndex: 100,
                width: 48,
                height: 48,
                fontSize: 20,
              }
            : undefined
        }
      >
        {inline ? (
          <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
        ) : (
          <PlusOutlined />
        )}
        {!isFloating &&
          (isChannelConfiguration
            ? tBots("buttons.new_configuration")
            : tBots("buttons.create_bot"))}
      </Button>
    );
    if (isDropdown) {
      return (
        <Dropdown
          title={tBots("buttons.create_bot")}
          menu={{
            items: [
              {
                label: (
                  <span data-testid={MY_BOTS_CREATE_SINGLE_BUTTON_ID}>
                    {tBots("buttons.create")}
                  </span>
                ),
                key: "single",
                icon: <UserAddOutlined />,
                onClick: () => onClick(),
              },
              {
                label: (
                  <span data-testid={MY_BOTS_BULK_CREATE_BUTTON_ID}>
                    {tBots("buttons.bulk_create")}
                  </span>
                ),
                key: "bulk",
                icon: <UsergroupAddOutlined />,
                onClick: () => onClick(true),
              },
            ],
          }}
          trigger={["click"]}
        >
          {button}
        </Dropdown>
      );
    }
    return button;
  };

  let filtersStore: FiltersSlice;
  if (isChannelConfiguration) {
    filtersStore = groupConfigurationsFilters;
  } else if (isPublicBots) {
    filtersStore = publicBotsFilters;
  } else {
    filtersStore = BOT_TYPE_TO_FILTERS_SLICE[botType];
  }

  usePageWrapper({
    filters: {
      actions: filtersStore.actions,
      selectors: filtersStore.selectors,
      loading: loading,
    },
    showSearch: true,
    hideMobileFilters: true,
    dependencies: [isPublicBots, loading],
  });

  const numActiveFilters = useSelector(
    filtersStore.selectors.selectNumActiveFilters
  );

  const sortSelect = useMemo(
    () =>
      isPublicBots ? undefined : (
        <SortSelect
          setSortBy={setSortBy}
          sortBy={sortBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          sortValues={sortOptions}
          rounded={true}
          isSingleFiltersChipsRow={isSingleFiltersChipsRow}
          labelMapper={(value) => tBots(value)}
        />
      ),
    [
      isPublicBots,
      sortBy,
      sortDirection,
      sortOptions,
      isSingleFiltersChipsRow,
      i18n.language,
    ]
  );

  const layoutChangeButtons = useMemo(
    () =>
      isPublicBots ? undefined : (
        <LayoutSwitch
          layoutIcons={[<MenuOutlined />, <TableOutlined />]}
          selectedLayoutIndex={isCardsView ? 1 : 0}
          onClick={() => setIsCardsView?.(!isCardsView)}
          style={{ height: 32 }}
        />
      ),
    [isPublicBots, isCardsView]
  );

  const getBulkActions = () => {
    if (!isBulkMode) return undefined;
    return (
      <BulkActions
        isAllSelected={isAllSelected}
        actionButtonsData={actionButtonsData}
        unselectAll={() => {
          setSelectedBotsIDs([]);
        }}
        selectAll={() => {
          onSelectUnselectAllPress("all");
        }}
        onCancel={exitBulkUpdate}
        numSelected={selectedBotsIDs.length}
        isBots={true}
      />
    );
  };

  const webhookUrlButton =
    !isReadOnly &&
    (botType === BotType.TRADING_VIEW ||
      botType === BotType.TRADING_VIEW_SIGNALS) ? (
      <CopyToClipboardButton
        name={tBots("labels.webhook_url")}
        text={getTvWebhookBaseUrl()}
        icon={
          isNewLookPage ? (
            <Copy size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
          ) : (
            <CopyOutlined />
          )
        }
        type={isNewLookPage ? "default" : "primary"}
        shape={undefined}
      >
        {tBots("labels.webhook_url")}
      </CopyToClipboardButton>
    ) : undefined;

  const bulkActionsRow = getBulkActions();
  const actionsRow = (
    <TopBarWrapper>
      <ActionsWrapper>
        {showSortInActionsRow && sortSelect}
        {!mobileSearch && !isSingleFiltersChipsRow && SearchInput}
        {!isSingleFiltersChipsRow && layoutChangeButtons}
        {!isSingleFiltersChipsRow && useFiltersChips && (
          <FiltersButtonWrapper>
            <Button
              onClick={() => setShowFilters?.(!showFilters)}
              loading={loading}
              id="bots-filters"
            >
              <FilterOutlined />
              {tBots("labels.filters")}
            </Button>
            <FiltersCounter numActiveFilters={numActiveFilters} />
          </FiltersButtonWrapper>
        )}
        {!isSingleFiltersChipsRow && webhookUrlButton}
        {!isSummaryTabs && getCreateBotButton()}
      </ActionsWrapper>
      <NoExchangeConnectedModal
        placement="bots_page"
        onClose={() => setNoExchangeModalVisible(false)}
        action={tBots("buttons.create_bot")}
        open={noExchangeModalVisible}
      />
    </TopBarWrapper>
  );

  const chipsExtraComponent = useMemo(() => {
    return (
      <>
        {!isBottomNavigator && layoutChangeButtons}
        {sortSelect}
      </>
    );
  }, [isBottomNavigator, layoutChangeButtons, sortSelect]);

  const chipsStyle = useMemo(() => {
    return {
      paddingBottom: isBottomNavigator ? 0 : padding,
      width: "100%",
      border: isSummaryTabs && !isBottomNavigator ? "none" : undefined,
    };
  }, [isBottomNavigator, padding, isSummaryTabs]);

  const chipsSingleRowStyle = useMemo(() => {
    return isSummaryTabs && !isBottomNavigator
      ? ({ flexWrap: "wrap", gap: 6 } as const)
      : undefined;
  }, [isSummaryTabs, isBottomNavigator]);

  const getExpandableChipsContent = () => {
    if (!filtersReady) return null;
    const filterChips = (
      <FiltersChips
        filtersActions={filtersStore.actions}
        filtersSelectors={filtersStore.selectors}
        isLoading={loading}
        filterTypeToTitle={botsFilterTypeToTitle}
        extraComponent={chipsExtraComponent}
        style={chipsStyle}
        isSingleRowView={isSummaryTabs}
        hideFiltersButton={isSummaryTabs}
        singleRowStyle={chipsSingleRowStyle}
      />
    );
    if (isBottomNavigator) return filterChips;
    return (
      <ExpandCollapse expanded={showFilters || isSingleFiltersChipsRow}>
        {!isPublicBots && !isSingleFiltersChipsRow && !showSortInActionsRow && (
          <Flex
            $justifyContent="flex-end"
            style={{ columnGap: 8, paddingBottom: padding }}
          >
            <StyledText>{tBots("labels.sort_by")}</StyledText>
            {sortSelect}
          </Flex>
        )}
        {filterChips}
      </ExpandCollapse>
    );
  };

  const newLookFiltersButton = useFiltersChips && !isSingleFiltersChipsRow && (
    <NewLookFiltersButton
      open={showFilters}
      numActiveFilters={numActiveFilters}
      loading={loading}
      onClick={() => setShowFilters?.(!showFilters)}
      onReset={() => dispatch(filtersStore.actions.selectAll(undefined))}
    />
  );

  // New look (desktop): on "My bots" every list control (search, filters) and
  // the page actions sit in one row in the bots list header, next to sorting
  // and layout; the page toolbar is only used while that header is hidden
  // (no bots yet). Public bots have no list header, so everything stays in
  // the page toolbar there.
  const newLookListControls = isNewLookPage && !isPublicBots;
  const newLookSearch = !mobileSearch && (
    <SearchWrapper>{SearchInput}</SearchWrapper>
  );

  const newLookPageActions = (
    <>
      {webhookUrlButton}
      {getCreateBotButton(true)}
    </>
  );
  // Next to an empty state, whose own button is the view's one action (the
  // top bar keeps "Create Bot" too): only the webhook URL stays.
  const newLookQuietPageActions = webhookUrlButton;
  const newLookPageActionsDeps = [
    loading,
    webhookUrlButton === undefined,
    canManageBotType,
    isBulkMode,
    botType,
    user,
    isChannelConfiguration,
    i18n.language,
  ];

  const newLookIntroActions = isNewLookPage && !newLookListControls && (
    <PageIntroActions
      actions={
        <>
          {newLookSearch}
          {newLookFiltersButton}
          {newLookPageActions}
        </>
      }
      deps={[
        searchValue,
        showFilters,
        numActiveFilters,
        mobileSearch,
        useFiltersChips,
        isSingleFiltersChipsRow,
        ...newLookPageActionsDeps,
      ]}
    />
  );

  if (isNewLookPage) {
    const filtersChips = (
      <FiltersChips
        filtersActions={filtersStore.actions}
        filtersSelectors={filtersStore.selectors}
        isLoading={loading}
        filterTypeToTitle={botsFilterTypeToTitle}
        style={{
          paddingBottom: newLookListControls ? 0 : NEW_LOOK_SECTION_GAP,
          width: "100%",
        }}
      />
    );
    const hasNewLookChips =
      filtersReady && (useFiltersChips || isSingleFiltersChipsRow);
    const newLookChips = hasNewLookChips && (
      <ExpandCollapse expanded={!!showFilters || isSingleFiltersChipsRow}>
        {filtersChips}
      </ExpandCollapse>
    );
    const content = (
      <FiltersColumnContentWrapper>
        <NewLookContent>
          {!newLookListControls && newLookChips}
          {bulkActionsRow && (
            <NewLookBulkActions>
              <NewLookBulkBar>{bulkActionsRow}</NewLookBulkBar>
            </NewLookBulkActions>
          )}
          {children}
        </NewLookContent>
        {filtersReady && !useFiltersChips && !isSingleFiltersChipsRow && (
          <FiltersColumn
            filtersActions={filtersStore.actions}
            filtersSelectors={filtersStore.selectors}
            expanded={showFilters}
            width={FILTERS_WIDTH}
            height={`calc(100vh - ${HEADER_HEIGHT + 160}px)`}
            style={{ marginRight: NEW_LOOK_PAGE_GUTTER, marginTop: 20 }}
            isLoading={loading}
            filterTypeToTitle={botsFilterTypeToTitle}
          />
        )}
      </FiltersColumnContentWrapper>
    );
    return (
      <>
        {newLookIntroActions}
        {newLookListControls ? (
          <NewLookBotsToolbarContext.Provider
            value={{
              search: newLookSearch,
              filtersButton: newLookFiltersButton,
              filtersChips: hasNewLookChips ? filtersChips : undefined,
              filtersOpen: !!showFilters || isSingleFiltersChipsRow,
              isFiltering: !!searchValue || numActiveFilters > 0,
              pageActions: newLookPageActions,
              quietPageActions: newLookQuietPageActions,
              pageActionsDeps: newLookPageActionsDeps,
            }}
          >
            {content}
          </NewLookBotsToolbarContext.Provider>
        ) : (
          content
        )}
        <NoExchangeConnectedModal
          placement="bots_page"
          onClose={() => setNoExchangeModalVisible(false)}
          action={tBots("buttons.create_bot")}
          open={noExchangeModalVisible}
        />
      </>
    );
  }

  if (isSingleFiltersChipsRow) {
    const headerContent = (
      <>
        {useFiltersChips && getExpandableChipsContent()}
        {bulkActionsRow && !isBottomNavigator && (
          <div style={{ padding }}>{bulkActionsRow}</div>
        )}
      </>
    );

    const header = isBottomNavigator ? (
      headerContent
    ) : (
      <StickyHeader style={{ paddingTop: padding, ...style }}>
        {headerContent}
      </StickyHeader>
    );
    const styledWebhookUrlButton = webhookUrlButton && (
      <Flex
        style={{
          justifyContent: "flex-end",
          padding: `0 ${padding}px ${padding}px`,
        }}
      >
        {webhookUrlButton}
      </Flex>
    );
    return (
      <>
        <div style={{ width: "fit-content" }}>{actionsRow}</div>
        {header}
        {!isBottomNavigator && styledWebhookUrlButton}
        <ContentWrapper
          style={{
            padding: `0 ${isBottomNavigator ? 0 : padding}px`,
          }}
          $isBottomNavigator={isBottomNavigator}
          $isSummaryTabs={isSummaryTabs}
        >
          {children}
        </ContentWrapper>
        {isBottomNavigator && bulkActionsRow}
      </>
    );
  }

  if (useFiltersChips) {
    return (
      <>
        <StickyHeader
          style={{
            padding: `${padding}px ${padding}px 0`,
          }}
        >
          <div style={{ paddingBottom: padding }}>{actionsRow}</div>
          {useFiltersChips && getExpandableChipsContent()}
          {bulkActionsRow && (
            <div style={{ paddingBottom: padding }}>{bulkActionsRow}</div>
          )}
        </StickyHeader>
        <ContentWrapper style={{ padding: `0 ${padding}px` }}>
          {children}
        </ContentWrapper>
      </>
    );
  }

  const contentDistanceTop =
    (isBulkMode ? 118 : 70) + HEADER_HEIGHT + (distanceTop || 0);
  const contentHeight = `calc(100vh - ${contentDistanceTop}px - ${padding}px)`;

  return (
    <>
      <StickyHeader style={{ padding }}>
        {actionsRow}
        {bulkActionsRow && (
          <div style={{ paddingTop: padding }}>{bulkActionsRow}</div>
        )}
      </StickyHeader>
      <FiltersColumnContentWrapper>
        <ContentWrapper style={{ padding: `0 ${padding}px` }}>
          {children}
        </ContentWrapper>
        <FiltersColumn
          filtersActions={filtersStore.actions}
          filtersSelectors={filtersStore.selectors}
          expanded={showFilters}
          width={FILTERS_WIDTH}
          height={contentHeight}
          style={{ marginRight: padding }}
          isLoading={loading}
          filterTypeToTitle={botsFilterTypeToTitle}
        />
      </FiltersColumnContentWrapper>
    </>
  );
};

export default React.memo(BotsPageWrapper);

const FILTERS_WIDTH = 300;

const TopBarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 8px;
`;
const ContentWrapper = styled.div<{
  $isBottomNavigator?: boolean;
  $isSummaryTabs?: boolean;
}>`
  flex: 1;
  ${(p) =>
    p.$isBottomNavigator &&
    `
      width: 100%;
      overflow-y: auto;
  `};
  ${(p) =>
    p.$isSummaryTabs &&
    `
    height: 100%;
  `};
`;
const FiltersColumnContentWrapper = styled.div`
  display: flex;
  width: 100%;
`;
const FiltersButtonWrapper = styled.div`
  position: relative;
`;
const NewLookContent = styled.div`
  flex: 1;
  min-width: 0;
  padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_SECTION_GAP}px;
`;
// Bulk mode: the selection bar stays on top while the list scrolls (the
// canvas behind it hides the cards passing under).
const NewLookBulkActions = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  margin: -8px 0 16px;
  padding: 8px 0;
  background: ${(p) => getNewLookTokens(p.theme).canvas};
`;
const NewLookBulkBar = styled.div`
  padding: 8px 12px 8px 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const SearchWrapper = styled.div`
  width: 240px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    width: 220px;
    @media (max-width: 1360px) {
      width: 180px;
    }
  `}
`;
const StyledText = styled(Text).attrs({ type: "secondary" })`
  white-space: nowrap;
  padding-right: 4px;
`;
